/**
 * §7.12 Wastewater Surveillance. Generates a "ground truth" incidence
 * series from a SEIRD run, builds synthetic observed RNA concentration
 * by convolving with the catalog shedding curve + noise, then runs
 * estimateIncidenceFromWastewater to recover the incidence. Shows
 * fidelity of recovery + estimated lead-time.
 */

import { useMemo, useState } from 'react';
import { findPathogen } from '@biomax/plugin-pathogen-database';
import { PinToBriefButton } from '../pack/PinToBriefButton';
import { simulateSeird } from '@biomax/plugin-epidemiology';
import { SHEDDING_CATALOG, estimateIncidenceFromWastewater, WASTEWATER_REFERENCES } from '@biomax/plugin-wastewater';
import { Sparkline } from '../sparkline';

const SUPPORTED = Object.keys(SHEDDING_CATALOG);

export function WastewaterSurveillance() {
  const [pathogenId, setPathogenId] = useState<string>(SUPPORTED[0] ?? 'sars-cov-2-wildtype');
  const [catchmentPop, setCatchmentPop] = useState<number>(1_000_000);
  const [noisePct, setNoisePct] = useState<number>(15);

  const view = useMemo(() => {
    const shedding = SHEDDING_CATALOG[pathogenId];
    const pathogen = findPathogen(pathogenId);
    if (!shedding || !pathogen) return null;

    // Ground-truth incidence from a 120-day SEIRD run with catchment pop.
    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;
    const sim = simulateSeird({
      N: catchmentPop, I0: 50, beta, gamma, sigma, ifr,
      durationDays: 120, dtDays: 1,
    });
    const S = sim.compartments.S!;
    const trueIncidence: number[] = [];
    for (let i = 1; i < S.length; i++) trueIncidence.push(Math.max(0, S[i - 1]! - S[i]!));

    // Forward-model the observed RNA: convolve true incidence with shedding kernel,
    // then add multiplicative log-normal noise (deterministic seed).
    const stoolGramsPerDay = shedding.stoolMassPerPersonGramsPerDay ?? 200;
    const sewageLPerDay = 200;
    const kernel = shedding.log10CopiesPerGram.map(l => (Math.pow(10, l) * stoolGramsPerDay) / sewageLPerDay);
    const n = trueIncidence.length;
    const cleanObserved: number[] = new Array(n).fill(0);
    for (let t = 0; t < n; t++) {
      let sum = 0;
      for (let s = 0; s < Math.min(kernel.length, t + 1); s++) {
        sum += (trueIncidence[t - s] ?? 0) * (kernel[s] ?? 0) / catchmentPop;
      }
      cleanObserved[t] = sum;
    }
    let seed = 99173;
    const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 0x100000000; };
    const noisy = cleanObserved.map(v => {
      const noise = 1 + ((rand() - 0.5) * 2 * noisePct) / 100;
      return Math.max(0, v * noise);
    });

    // Run the deconvolution.
    const recovered = estimateIncidenceFromWastewater({
      observedCopiesPerLiter: noisy,
      catchmentPopulation: catchmentPop,
      sheddingCurve: shedding,
    });

    // Goodness-of-fit: Pearson correlation between trueIncidence + recovered.
    const meanTrue = trueIncidence.reduce((a, b) => a + b, 0) / n;
    const meanRec = recovered.estimatedIncidence.reduce((a, b) => a + b, 0) / n;
    let num = 0, denomT = 0, denomR = 0;
    for (let i = 0; i < n; i++) {
      const dt = trueIncidence[i]! - meanTrue;
      const dr = recovered.estimatedIncidence[i]! - meanRec;
      num += dt * dr; denomT += dt * dt; denomR += dr * dr;
    }
    const pearsonR = (denomT > 0 && denomR > 0) ? num / Math.sqrt(denomT * denomR) : 0;

    // Lead-time: shift recovered series to find the lag that maximises correlation
    // with trueIncidence — that's how much the RNA signal "leads" cases.
    let bestLag = 0; let bestR = pearsonR;
    for (let lag = 1; lag <= 14; lag++) {
      let num2 = 0, dT = 0, dR = 0;
      for (let i = lag; i < n; i++) {
        const dt = trueIncidence[i]! - meanTrue;
        const dr = recovered.estimatedIncidence[i - lag]! - meanRec;
        num2 += dt * dr; dT += dt * dt; dR += dr * dr;
      }
      const r = (dT > 0 && dR > 0) ? num2 / Math.sqrt(dT * dR) : 0;
      if (r > bestR) { bestR = r; bestLag = lag; }
    }

    return {
      shedding, pathogen,
      trueIncidence, recovered, noisy,
      pearsonR, bestLag, bestR,
    };
  }, [pathogenId, catchmentPop, noisePct]);

  if (!view) return <div>No shedding curve bundled for this pathogen.</div>;

  const peakTrue = Math.max(...view.trueIncidence);
  const peakRec = Math.max(...view.recovered.estimatedIncidence);
  const peakRna = Math.max(...view.noisy);

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.12</span>
        <h1 className="surface__title">Wastewater Surveillance</h1>
      </div>
      <div className="surface__sub" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span>
          RNA → incidence deconvolution · {view.shedding.citations[0]?.source ?? 'multi-source'}
          {' '}· {view.shedding.citations[0]?.pmid && `PMID ${view.shedding.citations[0].pmid}`}
        </span>
        <span style={{ flex: 1 }} />
        <PinToBriefButton item={{
          id: `wastewater-${pathogenId}-pop${catchmentPop}-noise${noisePct}`,
          surface: '§7.12',
          label: `${pathogenId} wastewater · peak ${peakRna.toExponential(2)} RNA copies/L · ${catchmentPop.toLocaleString()} pop catchment`,
          value: peakRna,
          context: { pathogen: pathogenId, catchmentPop, noisePct, peakRna },
        }} />
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Sewershed</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen (shedding curve bundled)</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {SUPPORTED.map(id => <option key={id} value={id}>{id}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Catchment population</span>
            <input type="number" value={catchmentPop}
              onChange={e => setCatchmentPop(parseInt(e.target.value, 10) || 100000)} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Measurement noise · ±{noisePct}%</span>
            <input type="range" min="0" max="40" step="1" value={noisePct}
              onChange={e => setNoisePct(parseInt(e.target.value, 10))} />
          </label>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Recovery correlation</div>
          <div className={`kpi__value ${view.pearsonR > 0.8 ? 'kpi__value--accent' : view.pearsonR > 0.5 ? 'kpi__value--signal' : 'kpi__value--danger'}`}>
            r = {view.pearsonR.toFixed(2)}
          </div>
          <div className="kpi__sub">deconvolved vs ground-truth incidence</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Estimated lead time</div>
          <div className="kpi__value kpi__value--accent">{view.bestLag} d</div>
          <div className="kpi__sub">RNA signal vs clinical-case proxy</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak RNA copies / L</div>
          <div className="kpi__value">{peakRna.toExponential(1)}</div>
          <div className="kpi__sub">noisy observed (sewage)</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak true incidence</div>
          <div className="kpi__value kpi__value--signal">{Math.round(peakTrue).toLocaleString()}</div>
          <div className="kpi__sub">new infections/day</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak recovered</div>
          <div className="kpi__value">{Math.round(peakRec).toLocaleString()}</div>
          <div className="kpi__sub">{(peakRec / peakTrue * 100).toFixed(0)}% of true peak</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Observed RNA in sewage</span>
          <span className="panel__meta">gene copies / L · {noisePct}% multiplicative noise</span>
        </div>
        <Sparkline values={view.noisy} label="Wastewater RNA" stroke="var(--bm-signal)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Recovered vs true incidence</span>
          <span className="panel__meta">deconvolution fidelity</span>
        </div>
        <Sparkline values={view.trueIncidence} label="Ground truth incidence" stroke="var(--bm-accent)" />
        <Sparkline values={view.recovered.estimatedIncidence} label="Wastewater-derived nowcast" stroke="var(--bm-danger)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Verified citations</span>
          <span className="panel__meta">{WASTEWATER_REFERENCES.filter(r => r.pmid).length} PMIDs round-tripped via @biomax/plugin-literature-fetch</span>
        </div>
        <table className="dtable">
          <thead><tr><th>ID</th><th>Paper</th><th>PMID</th><th>Backs</th></tr></thead>
          <tbody>
            {WASTEWATER_REFERENCES.map(r => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td style={{ fontSize: 12 }}>{r.label}</td>
                <td className="num">{r.pmid ?? '—'}</td>
                <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>{r.appliesTo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Why this matters</span>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          Wastewater surveillance bypasses test-seeking-behaviour bias and leads
          clinical case detection by {view.bestLag} days at peak correlation
          (Pearson r = {view.bestR.toFixed(2)}). For pathogens that shed late
          (polio, MPXV) the lead is larger; for early-shedders the lead is shorter
          but signal-to-noise is higher. Each {view.shedding.log10CopiesPerGram.length}-day
          shedding curve is catalog-versioned with PMID citations.
        </div>
      </div>
    </div>
  );
}
